import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { IconButton, InlineButton } from '@platform/ui'
import cn from 'classnames'
import { useFocusTrap } from '@/features/wa/focusTrap/useFocusTrap'
import { useFilterCategories } from '@/hooks/useFilterCategories/useFilterCategories'
import { usePublicFilterCategories } from '@/hooks/usePublicFilterCategories'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { createFiltersSelector, setFilters } from '@/store/slices/aggregatesSlice'
import { CourseFilterCategory } from './CourseFilterCategory'
import { CourseFilterCollection } from './CourseFilterCollection'
import { CourseFilterPriority } from './CourseFilterPriority'
import { CourseFilterProgress } from './CourseFilterProgress'
import styles from './CourseFiltersDrawer.module.scss'
import { CourseFilterTerm } from './CourseFilterTerm'
import { FilterDrawerType, FilterType } from './types'

type Props = {
  type: FilterDrawerType
  isOpen: boolean
  onClose: () => void
}

type Category = {
  id: string
  name: string
  attributes: Array<{ id: string; name: string }>
}

type FilterConfig = {
  component: React.ComponentType<{
    type: FilterDrawerType
    onChange: (filterType: FilterType, value: string) => void
    category?: Category
  }>
  label: string
  filterType: string
  props?: Record<string, any>
}

export const CourseFiltersDrawer = ({ type, isOpen, onClose }: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const filters = useAppSelector((state) => createFiltersSelector(state, type))

  const getCategoryFilters = type === 'public' ? usePublicFilterCategories : useFilterCategories
  const { filterCategories } = getCategoryFilters(type)

  const sortedFilters = useMemo(() => {
    const filterConfigs: FilterConfig[] = []

    if (type === 'enrolled') {
      filterConfigs.push({
        component: CourseFilterProgress,
        label: t('coursesFilterDrawer.courseProgress'),
        filterType: 'courseProgress',
      })

      filterConfigs.push({
        component: CourseFilterPriority,
        label: t('coursesFilterDrawer.coursePriority'),
        filterType: 'coursePriority',
      })
    }

    if (['enrolled', 'catalog'].includes(type)) {
      filterConfigs.push({
        component: CourseFilterTerm,
        label: t('coursesFilterDrawer.term'),
        filterType: 'term',
      })
    }

    filterCategories.forEach((category: Category) => {
      filterConfigs.push({
        component: CourseFilterCategory,
        label: category.name,
        filterType: `category[${category.id}]`,
        props: { category },
      })
    })

    filterConfigs.push({
      component: CourseFilterCollection,
      label: t('coursesFilterDrawer.collection'),
      filterType: 'collection',
    })

    return filterConfigs.sort((a, b) => a.label.localeCompare(b.label))
  }, [type, t, filterCategories])

  const syncUrlFilters = useCallback(
    (key: string, value: string[]) => {
      const searchParams = new URLSearchParams(location.search)

      if (value.length > 0) {
        searchParams.set(key, value.join(','))
      } else {
        searchParams.delete(key)
      }

      navigate({ search: searchParams.toString() }, { replace: true })
    },
    [location.search, navigate],
  )

  const handleCheckboxChange = useCallback(
    (filterType: FilterType, value: string) => {
      // Handle category filters with format "category[categoryId]"
      if (typeof filterType === 'string' && filterType.startsWith('category[') && filterType.endsWith(']')) {
        const categoryId = filterType.slice(9, -1) // Extract categoryId from "category[categoryId]"
        const currentAttributes = filters.category[categoryId] || []
        const updatedAttributes = currentAttributes.includes(value)
          ? currentAttributes.filter((item) => item !== value)
          : [...currentAttributes, value]

        const updatedFilters = {
          ...filters,
          category: {
            ...filters.category,
            [categoryId]: updatedAttributes,
          },
        }

        dispatch(setFilters({ type, filters: updatedFilters }))
        syncUrlFilters(filterType, updatedAttributes)
        return
      }

      // Handle regular filter types
      const currentFilter = filters[filterType as keyof typeof filters] as string[]
      const updatedFilter = currentFilter.includes(value)
        ? currentFilter.filter((item) => item !== value)
        : [...currentFilter, value]

      dispatch(setFilters({ type, filters: { ...filters, [filterType]: updatedFilter } }))
      syncUrlFilters(filterType, updatedFilter)
    },
    [dispatch, filters, syncUrlFilters, type],
  )

  const handleClearFilters = useCallback(() => {
    dispatch(
      setFilters({
        type,
        filters: { courseProgress: [], coursePriority: [], term: [], collection: [], category: {} },
      }),
    )

    const searchParams = new URLSearchParams(location.search)
    searchParams.forEach((_, key) => {
      searchParams.delete(key)
    })
    navigate({ search: '' }, { replace: true })
  }, [dispatch, location.search, navigate, type])

  const shouldDisableClear = useMemo(() => {
    const { category, ...otherFilters } = filters
    const hasRegularFilters = Object.values(otherFilters).some((filter) => filter.length > 0)
    const hasCategoryFilters = category ? Object.values(category).some((attributes) => attributes.length > 0) : false
    return !hasRegularFilters && !hasCategoryFilters
  }, [filters])

  const tabIndex = isOpen ? 0 : -1

  const ref = useFocusTrap(isOpen)

  return (
    <div className={cn(styles.drawer, { [styles.open]: isOpen })} tabIndex={tabIndex} ref={ref}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>{t('coursesFilterDrawer.filters')}</div>
        <IconButton
          shape="rounded"
          aria-label="Close drawer"
          iconName="CloseBoldStroke"
          color="tertiary"
          onClick={onClose}
          testId="close-drawer"
        />
      </div>

      <div className={styles.container}>
        <InlineButton
          iconBeforeText="FilterRemoveBoldStroke"
          text={t('coursesFilterDrawer.clearFilters')}
          onClick={handleClearFilters}
          color="neutral"
          size="small"
          disabled={shouldDisableClear}
          testId="clear-filters"
        />
      </div>

      {sortedFilters.map((filterConfig) => {
        const FilterComponent = filterConfig.component
        return (
          <FilterComponent
            key={filterConfig.filterType}
            type={type}
            onChange={handleCheckboxChange}
            {...(filterConfig.props || {})}
          />
        )
      })}
    </div>
  )
}
